import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Link2, Loader2, Pencil, Save, Sparkles, X } from 'lucide-react';
import { api, streamSse } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import type { DirectorCharacterCard, DirectorLocation, DirectorNpcCard, DirectorProp, DirectorPlan } from './types';


function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function textToHighlightedHtml(text: string, mentionRefs: MentionRef[]): string {
  const labels = Array.from(new Set(mentionRefs.map((m) => m.label).filter(Boolean)))
    .sort((a, b) => b.length - a.length);

  if (labels.length === 0) {
    return escapeHtml(text)
      .replace(/(@[\w\u4e00-\u9fff·-]+)/g, '<mark class="mention-highlight">$1</mark>')
      .replace(/\n/g, '<br>');
  }

  const mentionPattern = new RegExp(`@(?:${labels.map(escapeRegExp).join('|')})`, 'g');
  let html = '';
  let cursor = 0;
  for (const match of text.matchAll(mentionPattern)) {
    const start = match.index ?? 0;
    html += escapeHtml(text.slice(cursor, start));
    html += `<mark class="mention-highlight">${escapeHtml(match[0])}</mark>`;
    cursor = start + match[0].length;
  }
  html += escapeHtml(text.slice(cursor));
  return html.replace(/\n/g, '<br>');
}

function extractPlainText(el: HTMLDivElement): string {
  let result = '';
  function walk(node: Node) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const elem = node as Element;
      if (elem.hasAttribute('data-shot-refs') || elem.hasAttribute('data-label')) return;
      const tag = elem.tagName.toLowerCase();
      if (tag === 'br') { result += '\n'; return; }
      if (tag === 'div' || tag === 'p') {
        if (result.length > 0 && !result.endsWith('\n')) result += '\n';
      }
      for (const child of Array.from(node.childNodes)) walk(child);
    } else if (node.nodeType === Node.TEXT_NODE) {
      result += node.textContent ?? '';
    }
  }
  for (const child of Array.from(el.childNodes)) walk(child);
  return result;
}

export interface MentionRef {
  label: string;
  imageUrl: string;
}

interface Props {
  episodeId: string;
  episodeIndex: number;
  sceneId: string;
  initialPrompt: string;
  initialMentions?: MentionRef[];
  characters?: DirectorCharacterCard[];
  npcs?: DirectorNpcCard[];
  locations?: DirectorLocation[];
  props?: DirectorProp[];
  prevEndingFrameUrl?: string | null;
  usePrevEndingFrame?: boolean;
  onUsePrevEndingFrameChange?: (value: boolean | ((prev: boolean) => boolean)) => void;
}

type Level1 = 'character' | 'npc' | 'location' | 'prop';

interface MenuItem {
  id: string;
  label: string;
  imageUrl?: string | null;
  children?: MenuItem[];
}

function buildMenu(
  characters: DirectorCharacterCard[],
  npcs: DirectorNpcCard[],
  locations: DirectorLocation[],
  props: DirectorProp[],
): { key: Level1; label: string; items: MenuItem[] }[] {
  const charItems: MenuItem[] = characters.map((c) => {
    const looks = c.looks ?? [];
    const validLooks = looks.filter((lk) => lk.profileImageUrl);
    const children: MenuItem[] = [];
    if (c.profileImageUrl) {
      children.push({ id: `${c.id}:__default__`, label: '标准形象', imageUrl: c.profileImageUrl });
    }
    for (const lk of validLooks) {
      children.push({ id: `${c.id}:${lk.id}`, label: lk.label, imageUrl: lk.profileImageUrl });
    }
    if (children.length === 0) {
      return { id: c.id, label: c.name, imageUrl: null };
    }
    return { id: c.id, label: c.name, imageUrl: children[0].imageUrl, children };
  });

  const npcItems: MenuItem[] = npcs.map((n) => {
    const looks = n.looks ?? [];
    const validLooks = looks.filter((lk) => lk.profileImageUrl);
    const children: MenuItem[] = [];
    if (n.profileImageUrl) {
      children.push({ id: `${n.id}:__default__`, label: '标准形象', imageUrl: n.profileImageUrl });
    }
    for (const lk of validLooks) {
      children.push({ id: `${n.id}:${lk.id}`, label: lk.label, imageUrl: lk.profileImageUrl });
    }
    if (children.length === 0) {
      return { id: n.id, label: n.name, imageUrl: null };
    }
    return { id: n.id, label: n.name, imageUrl: children[0].imageUrl, children };
  });

  const locItems: MenuItem[] = locations.map((l) => {
    const lookItems: MenuItem[] = [];

    const baseImages = (l.lockedImages ?? []).filter((li) => li.url);
    const baseEstablishing = baseImages.find((li) => li.variant === 'establishing')?.url ?? l.lockedImageUrl ?? null;
    if (baseEstablishing) {
      lookItems.push({
        id: `${l.id}:__base__`,
        label: '定场',
        imageUrl: baseEstablishing,
      });
    }

    for (const look of l.looks ?? []) {
      const lookImages = (look.lockedImages ?? []).filter((li) => li.url);
      const lookEstablishing = lookImages.find((li) => li.variant === 'establishing')?.url ?? null;
      if (!lookEstablishing) continue;
      lookItems.push({
        id: `${l.id}:${look.id}`,
        label: look.label,
        imageUrl: lookEstablishing,
      });
    }

    const fallbackUrl =
      baseEstablishing ??
      l.lockedImageUrl ?? null;

    if (lookItems.length === 0) {
      return { id: l.id, label: l.name, imageUrl: fallbackUrl };
    }
    if (lookItems.length === 1 && (lookItems[0].children?.length ?? 0) <= 1) {
      return { id: l.id, label: l.name, imageUrl: fallbackUrl, children: lookItems[0].children };
    }
    return { id: l.id, label: l.name, imageUrl: fallbackUrl, children: lookItems };
  });

  const propItems: MenuItem[] = props.map((p) => {
    const baseUrl = p.lockedImageUrl ?? null;
    const sizeSuffix = p.size ? `(${p.size})` : '';
    const propLabel = `${p.name}${sizeSuffix}`;
    const children: MenuItem[] = [];
    if (baseUrl) {
      children.push({ id: `${p.id}:__base__`, label: '基础', imageUrl: baseUrl });
    }
    for (const look of p.looks ?? []) {
      const lookUrl = look.lockedImageUrl ?? null;
      if (lookUrl) {
        children.push({ id: `${p.id}:${look.id}`, label: look.label, imageUrl: lookUrl });
      }
    }
    if (children.length === 0) {
      return { id: p.id, label: propLabel, imageUrl: baseUrl };
    }
    return { id: p.id, label: propLabel, imageUrl: children[0].imageUrl, children };
  });

  return [
    { key: 'character', label: '角色', items: charItems },
    { key: 'npc', label: 'NPC', items: npcItems },
    { key: 'location', label: '场景', items: locItems },
    { key: 'prop', label: '道具', items: propItems },
  ];
}

export function CameraPromptField({
  episodeId,
  episodeIndex,
  sceneId,
  initialPrompt,
  initialMentions = [],
  characters = [],
  npcs = [],
  locations = [],
  props = [],
  prevEndingFrameUrl = null,
  usePrevEndingFrame = false,
  onUsePrevEndingFrameChange,
}: Props) {
  const qc = useQueryClient();
  const [value, setValue] = useState(initialPrompt);
  const [mentions, setMentionsState] = useState<MentionRef[]>(initialMentions);
  const mentionsRef = useRef<MentionRef[]>(initialMentions);
  const setMentions = useCallback((next: MentionRef[] | ((prev: MentionRef[]) => MentionRef[])) => {
    const resolved = typeof next === 'function' ? next(mentionsRef.current) : next;
    mentionsRef.current = resolved;
    setMentionsState(resolved);
  }, []);
  const [streaming, setStreaming] = useState(false);
  const [aiDirty, setAiDirty] = useState(false);
  const aiGeneratedRef = useRef(false);
  const streamCtrlRef = useRef<AbortController | null>(null);
  const prevSceneId = useRef(sceneId);
  const editorRef = useRef<HTMLDivElement>(null);
  const isComposingRef = useRef(false);
  const pendingSelectionRef = useRef<number | null>(null);

  const [atOpen, setAtOpen] = useState(false);
  const [atQuery, setAtQuery] = useState('');
  const atStartRef = useRef<number>(-1);
  const [level1, setLevel1] = useState<Level1 | null>(null);
  const [level2Item, setLevel2Item] = useState<MenuItem | null>(null);
  const [level3Item, setLevel3Item] = useState<MenuItem | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editInstruction, setEditInstruction] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  function closeAtMenu() {
    setAtOpen(false);
    setLevel1(null);
    setLevel2Item(null);
    setLevel3Item(null);
  }

  function goBack() {
    if (level3Item) { setLevel3Item(null); return; }
    if (level2Item) { setLevel2Item(null); return; }
    setLevel1(null);
  }

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = textToHighlightedHtml(initialPrompt, mentionsRef.current);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (prevSceneId.current !== sceneId) {
      prevSceneId.current = sceneId;
      setValue(initialPrompt);
      setMentions(initialMentions);
      setAtOpen(false);
      setEditOpen(false);
      setEditInstruction('');
      setAiDirty(false);
      aiGeneratedRef.current = false;
      if (editorRef.current) {
        editorRef.current.innerHTML = textToHighlightedHtml(initialPrompt, mentionsRef.current);
      }
    }
  }, [sceneId, initialPrompt, initialMentions]);

  const basePath = `director/episodes/${episodeId}/director/episodes/${episodeIndex}/scenes/${sceneId}/camera-prompt`;
  const continuityAvailable = Boolean(prevEndingFrameUrl && onUsePrevEndingFrameChange);

  function patchPlanCache(cameraPrompt: string | null, savedMentions?: MentionRef[]) {
    qc.setQueryData<DirectorPlan>(['director', 'plan', episodeId], (old) => {
      if (!old) return old;
      return {
        ...old,
        episodes: old.episodes.map((ep) => ({
          ...ep,
          scenes: ep.scenes.map((sc) =>
            sc.sceneId === sceneId
              ? { ...sc, cameraPrompt, mentionRefs: savedMentions ?? sc.mentionRefs }
              : sc,
          ),
        })),
      };
    });
    qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
  }

  const saveMut = useMutation({
    mutationFn: async () => {
      return api
        .patch(basePath, { json: { cameraPrompt: value, mentionRefs: mentions } })
        .json<{ sceneId: string; cameraPrompt: string | null }>();
    },
    onSuccess: (res) => {
      aiGeneratedRef.current = false;
      patchPlanCache(res.cameraPrompt ?? null, mentions);
      setAiDirty(false);
      toast.success('分镜镜头提示词已保存');
    },
    onError: (e: Error) => toast.error('保存失败', e.message || '请重试'),
  });

  function handleGenerate() {
    if (streaming) return;
    setValue('');
    setMentions([]);
    aiGeneratedRef.current = true;
    setStreaming(true);
    setAiDirty(false);
    let finalPrompt = '';
    const ctrl = streamSse<{ chunk?: string; done?: boolean; cameraPrompt?: string | null; mentionRefs?: MentionRef[]; error?: string }>(
      `${basePath}/generate`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          usePrevEndingFrame: continuityAvailable ? usePrevEndingFrame : false,
        }),
      },
      (data) => {
        if (data.chunk) {
          setValue((prev) => prev + data.chunk);
          finalPrompt += data.chunk;
        }
        if (data.done) {
          const generated = data.cameraPrompt ?? finalPrompt.trim();
          const generatedMentions = data.mentionRefs ?? mentionsRef.current;
          setMentions(generatedMentions);
          setValue(generated);
          if (editorRef.current) {
            editorRef.current.innerHTML = textToHighlightedHtml(generated, generatedMentions);
          }
          patchPlanCache(generated, generatedMentions);
          setAiDirty(true);
          setStreaming(false);
        }
        if (data.error) {
          toast.error('生成失败', data.error);
          setStreaming(false);
        }
      },
      (err) => {
        if ((err as Error)?.name !== 'AbortError') {
          toast.error('生成失败', String(err).slice(0, 200) || '请重试');
        }
        setStreaming(false);
      },
      () => setStreaming(false),
    );
    streamCtrlRef.current = ctrl;
  }

  function handleEdit() {
    const instruction = editInstruction.trim();
    if (!instruction || streaming) return;
    if (!value.trim()) { toast.error('请先生成或输入分镜脚本'); return; }
    setStreaming(true);
    setAiDirty(false);
    aiGeneratedRef.current = true;
    let finalPrompt = '';
    const ctrl = streamSse<{ chunk?: string; done?: boolean; cameraPrompt?: string | null; error?: string }>(
      `${basePath}/edit`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userInstruction: instruction }) },
      (data) => {
        if (data.chunk) {
          setValue((prev) => prev + data.chunk);
          finalPrompt += data.chunk;
        }
        if (data.done) {
          const edited = data.cameraPrompt ?? finalPrompt.trim();
          setValue(edited);
          patchPlanCache(edited, mentionsRef.current);
          setAiDirty(true);
          setStreaming(false);
          setEditOpen(false);
          setEditInstruction('');
        }
        if (data.error) {
          toast.error('修改失败', data.error);
          setStreaming(false);
        }
      },
      (err) => {
        if ((err as Error)?.name !== 'AbortError') {
          toast.error('修改失败', String(err).slice(0, 200) || '请重试');
        }
        setStreaming(false);
      },
      () => setStreaming(false),
    );
    streamCtrlRef.current = ctrl;
  }

  useEffect(() => {
    return () => { streamCtrlRef.current?.abort(); };
  }, []);

  const getCaretCharOffset = useCallback((el: HTMLDivElement): number => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return 0;
    const selRange = sel.getRangeAt(0);
    let charCount = 0;
    let found = false;
    const targetNode = selRange.startContainer;
    const targetOffset = selRange.startOffset;
    function walk(node: Node) {
      if (found) return;
      if (node.nodeType === Node.TEXT_NODE) {
        if (node === targetNode) {
          charCount += targetOffset;
          found = true;
        } else {
          charCount += node.textContent?.length ?? 0;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = (node as Element).tagName.toLowerCase();
        if (tag === 'br') {
          if (node === targetNode || node.parentNode === targetNode) {
            found = true;
          } else {
            charCount += 1;
          }
          return;
        }
        for (const child of Array.from(node.childNodes)) walk(child);
      }
    }
    walk(el);
    return charCount;
  }, []);

  const restoreCaretAt = useCallback((el: HTMLDivElement, offset: number) => {
    const sel = window.getSelection();
    if (!sel) return;
    const range = document.createRange();
    let charCount = 0;
    let found = false;
    function walk(node: Node) {
      if (found) return;
      if (node.nodeType === Node.TEXT_NODE) {
        const len = node.textContent?.length ?? 0;
        if (charCount + len >= offset) {
          range.setStart(node, offset - charCount);
          range.collapse(true);
          found = true;
        } else {
          charCount += len;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = (node as Element).tagName.toLowerCase();
        if (tag === 'br') {
          if (charCount + 1 >= offset) {
            range.setStartBefore(node);
            range.collapse(true);
            found = true;
          } else {
            charCount += 1;
          }
          return;
        }
        for (const child of Array.from(node.childNodes)) walk(child);
      }
    }
    walk(el);
    if (!found) {
      range.selectNodeContents(el);
      range.collapse(false);
    }
    sel.removeAllRanges();
    sel.addRange(range);
  }, []);

  const handleEditorInput = useCallback(() => {
    if (isComposingRef.current) return;
    const el = editorRef.current;
    if (!el) return;

    const plain = extractPlainText(el);
    setValue(plain);
    setAiDirty(false);

    const caretOffset = pendingSelectionRef.current ?? getCaretCharOffset(el);
    pendingSelectionRef.current = null;

    const atIdx = plain.lastIndexOf('@', caretOffset - 1);
    const isTypingMention =
      atIdx >= 0 &&
      caretOffset - atIdx <= 20 &&
      !/\s/.test(plain.slice(atIdx + 1, caretOffset));

    if (!isTypingMention && plain.includes('@')) {
      el.innerHTML = textToHighlightedHtml(plain, mentionsRef.current);
      restoreCaretAt(el, caretOffset);
    }

    if (isTypingMention) {
      atStartRef.current = atIdx;
      setAtQuery(plain.slice(atIdx + 1, caretOffset));
      setAtOpen(true);
      setLevel1(null);
      setLevel2Item(null);
      setLevel3Item(null);
    } else {
      setAtOpen(false);
    }
  }, [getCaretCharOffset, restoreCaretAt]);

  function insertMention(label: string, imageUrl: string) {
    const el = editorRef.current;
    if (!el) return;
    const atPos = atStartRef.current;

    const cursorPos = atPos + 1 + atQuery.length;

    const charName = label.split('·')[0].trim();
    const textBefore = value.slice(0, atPos);
    const endsWithName = charName.length >= 2 && textBefore.endsWith(charName);
    const insertStart = endsWithName ? atPos - charName.length : atPos;
    const prefix = endsWithName ? charName : '';
    const sliceFrom = Math.max(cursorPos, atPos + 1);
    const newVal = value.slice(0, insertStart) + `${prefix}@${label} ` + value.slice(sliceFrom);
    const newCursorPos = insertStart + prefix.length + label.length + 2;

    setValue(newVal);
    setAiDirty(false);
    setMentions((prev) => {
      if (prev.some((m) => m.label === label)) return prev;
      return [...prev, { label, imageUrl }];
    });
    closeAtMenu();

    const updatedMentions = mentionsRef.current.some((m) => m.label === label)
      ? mentionsRef.current
      : [...mentionsRef.current, { label, imageUrl }];
    el.innerHTML = textToHighlightedHtml(newVal, updatedMentions);
    restoreCaretAt(el, newCursorPos);
    el.focus();
  }

  function removeMention(label: string) {
    setMentions((prev) => prev.filter((m) => m.label !== label));
    const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    setValue((v) => v.replace(new RegExp(`@${escaped}\\s?`, 'g'), ''));
  }

  const menu = buildMenu(characters, npcs, locations, props);
  const savedValue = initialPrompt;
  const savedMentionKeys = (initialMentions ?? []).map((m) => m.label).sort().join(',');
  const currentMentionKeys = mentions.map((m) => m.label).sort().join(',');
  const dirty = (value !== savedValue || aiDirty || currentMentionKeys !== savedMentionKeys) && !streaming;

  const filteredLevel1 = menu.filter(
    (g) => g.items.length > 0 && (!atQuery || g.label.includes(atQuery) || g.items.some((it) => it.label.includes(atQuery))),
  );

  return (
    <section className="rounded-lg border border-outline-variant/30 bg-white px-3 py-2.5">
      <header className="mb-1.5 flex items-center justify-between gap-2">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
          分镜镜头提示词
        </h4>
        <div className="flex items-center gap-1.5">
          {continuityAvailable && (
            <button
              type="button"
              onClick={() => onUsePrevEndingFrameChange?.((v) => !v)}
              disabled={streaming}
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] transition ${
                usePrevEndingFrame
                  ? 'border-violet-400 bg-violet-50 text-violet-700'
                  : 'border-outline-variant/40 text-neutral-500 hover:border-violet-300'
              } ${streaming ? 'cursor-not-allowed opacity-60' : ''}`}
              title="AI 生成分镜时在镜头1前加入镜头0衔接镜头"
            >
              <Link2 className="h-3 w-3" />
              {usePrevEndingFrame ? '已开启衔接' : '衔接上镜'}
            </button>
          )}
          {dirty && (
            <Button size="sm" variant="agent" agentColor="director" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
              {saveMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              保存
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setEditOpen((v) => !v); setTimeout(() => editInputRef.current?.focus(), 50); }}
            disabled={streaming || !value.trim()}
            title="AI 局部修改"
          >
            <Pencil className="h-3 w-3" />
            AI 修改
          </Button>
          <Button size="sm" variant="agent" agentColor="director" onClick={handleGenerate} disabled={streaming}>
            {streaming ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            AI 生成
          </Button>
        </div>
      </header>

      {editOpen && (
        <div className="mb-2 flex items-center gap-1.5 rounded-md border border-brand-100 bg-brand-50/40 px-2 py-1.5">
          <input
            ref={editInputRef}
            type="text"
            value={editInstruction}
            onChange={(e) => setEditInstruction(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !streaming) handleEdit(); if (e.key === 'Escape') setEditOpen(false); }}
            placeholder="描述你要修改的内容，如：把第2镜改成特写，加强情绪…"
            className="min-w-0 flex-1 rounded border border-outline-variant/40 bg-white px-2 py-1 text-[11px] text-neutral-700 placeholder:text-neutral-400 focus:border-brand-300 focus:outline-none"
          />
          <Button size="sm" variant="agent" agentColor="director" onClick={handleEdit} disabled={streaming || !editInstruction.trim()}>
            {streaming ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            修改
          </Button>
          <button type="button" onClick={() => setEditOpen(false)} className="text-neutral-400 hover:text-neutral-600">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {mentions.length > 0 && (
        <div className="mb-1.5 flex flex-wrap gap-1">
          {mentions.map((m) => (
            <span
              key={m.label}
              className="inline-flex items-center gap-1 rounded-full border border-brand-200 bg-brand-50 pl-0.5 pr-2 py-0.5 text-[11px] text-brand-700"
            >
              {m.imageUrl && (
                <img src={m.imageUrl} alt={m.label} className="h-4 w-4 rounded-full object-cover" />
              )}
              @{m.label}
              <button type="button" onClick={() => removeMention(m.label)} className="ml-0.5 rounded-full text-brand-400 hover:text-brand-700">
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <div
          ref={editorRef}
          contentEditable={!streaming}
          suppressContentEditableWarning
          onInput={handleEditorInput}
          onCompositionStart={() => { isComposingRef.current = true; }}
          onCompositionEnd={() => {
            isComposingRef.current = false;
            handleEditorInput();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && atOpen) { setAtOpen(false); e.preventDefault(); }
          }}
          data-placeholder="输入 @ 选择角色/NPC/场景/道具形象，或点「AI 生成」自动生成…"
          className={[
            'min-h-[13rem] w-full resize-none rounded-md border border-outline-variant/30 bg-surface-variant/10 px-2 py-1.5',
            'text-[12px] leading-relaxed text-neutral-700 caret-neutral-700 outline-none whitespace-pre-wrap break-words',
            'focus:border-brand-300 focus:ring-1 focus:ring-brand-200',
            'empty:before:content-[attr(data-placeholder)] empty:before:text-neutral-400 empty:before:pointer-events-none',
            streaming ? 'cursor-not-allowed opacity-60' : '',
          ].join(' ')}
        />
      </div>

      {atOpen && createPortal(
        <>
          <div
            className="fixed inset-0 z-[200]"
            onMouseDown={(e) => { e.preventDefault(); closeAtMenu(); }}
          />
          <div
            className="fixed left-1/2 top-1/2 z-[201] w-[480px] max-w-[90vw] max-h-[70vh] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl border border-outline-variant/40 bg-white shadow-2xl flex flex-col"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-outline-variant/20 px-4 py-3">
              <span className="text-sm font-semibold text-neutral-800">
                {level3Item
                  ? `${level2Item?.label} · 选择角度`
                  : level2Item
                  ? level2Item.label
                  : level1
                  ? (menu.find((g) => g.key === level1)?.label ?? '选择')
                  : '插入参考形象'}
              </span>
              <div className="flex items-center gap-2">
                {(level3Item || level2Item || level1) && (
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); goBack(); }}
                    className="flex items-center gap-1 rounded px-2 py-0.5 text-[11px] text-neutral-400 hover:bg-surface-variant/40 hover:text-neutral-600"
                  >
                    <ChevronRight className="h-3 w-3 rotate-180" /> 返回
                  </button>
                )}
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); closeAtMenu(); }}
                  className="rounded p-0.5 text-neutral-400 hover:text-neutral-600"
                  aria-label="关闭"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {level3Item ? (
                <div className="grid grid-cols-3 gap-2 p-3">
                  {(level3Item.children ?? []).map((child) => (
                    <button
                      key={child.id}
                      type="button"
                      className="flex flex-col items-center gap-1.5 rounded-lg border border-outline-variant/30 p-2 text-center transition hover:border-brand-300 hover:bg-brand-50"
                      onMouseDown={(e) => { e.preventDefault(); insertMention(`${level2Item!.label}·${level3Item.label}·${child.label}`, child.imageUrl ?? ''); }}
                    >
                      {child.imageUrl ? (
                        <img src={child.imageUrl} alt={child.label} className="aspect-video w-full rounded-md object-cover" />
                      ) : (
                        <div className="aspect-video w-full rounded-md bg-neutral-100" />
                      )}
                      <span className="text-[11px] text-neutral-700">{child.label}</span>
                    </button>
                  ))}
                </div>
              ) : level2Item ? (
                <div className="grid grid-cols-2 gap-2 p-3">
                  {(level2Item.children ?? []).map((child) => (
                    <button
                      key={child.id}
                      type="button"
                      className="flex items-center gap-2.5 rounded-lg border border-outline-variant/30 p-2 text-left transition hover:border-brand-300 hover:bg-brand-50"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (child.children?.length) {
                          setLevel3Item(child);
                        } else {
                          const label = `${level2Item.label}·${child.label}`;
                          insertMention(label, child.imageUrl ?? '');
                        }
                      }}
                    >
                      {child.imageUrl ? (
                        <img src={child.imageUrl} alt={child.label} className="h-12 w-12 rounded-md object-cover shrink-0" />
                      ) : (
                        <div className="h-12 w-12 shrink-0 rounded-md bg-neutral-100" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[12px] font-medium text-neutral-700">{child.label}</p>
                        {child.children?.length ? (
                          <p className="text-[10px] text-neutral-400">{child.children.length} 个角度 →</p>
                        ) : null}
                      </div>
                    </button>
                  ))}
                </div>
              ) : level1 ? (
                <div className="grid grid-cols-2 gap-2 p-3">
                  {(menu.find((g) => g.key === level1)?.items ?? [])
                    .filter((it) => !atQuery || it.label.includes(atQuery))
                    .map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className="flex items-center gap-2.5 rounded-lg border border-outline-variant/30 p-2 text-left transition hover:border-brand-300 hover:bg-brand-50"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          if (item.children?.length) {
                            setLevel2Item(item);
                          } else {
                            insertMention(item.label, item.imageUrl ?? '');
                          }
                        }}
                      >
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt={item.label} className="h-12 w-12 rounded-md object-cover shrink-0" />
                        ) : (
                          <div className="h-12 w-12 shrink-0 rounded-md bg-neutral-100" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[12px] font-medium text-neutral-700">{item.label}</p>
                          {item.children?.length ? (
                            <p className="text-[10px] text-neutral-400">
                              {level1 === 'location' ? `${item.children.length} 个形象 →` : `${item.children.length} 个形象 →`}
                            </p>
                          ) : null}
                        </div>
                      </button>
                    ))}
                </div>
              ) : (
                <div className="p-3 space-y-1">
                  <div className="mb-2 text-[11px] text-neutral-400 px-1">选择类型</div>
                  {filteredLevel1.map((g) => (
                    <button
                      key={g.key}
                      type="button"
                      className="flex w-full items-center justify-between rounded-lg border border-outline-variant/30 px-4 py-3 text-left transition hover:border-brand-300 hover:bg-brand-50"
                      onMouseDown={(e) => { e.preventDefault(); setLevel1(g.key); }}
                    >
                      <span className="text-[13px] font-medium text-neutral-700">{g.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-neutral-400">{g.items.length} 个</span>
                        <ChevronRight className="h-4 w-4 text-neutral-400" />
                      </div>
                    </button>
                  ))}
                  {filteredLevel1.length === 0 && (
                    <div className="px-3 py-6 text-center text-[12px] text-neutral-400">暂无可用形象</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>,
        document.body,
      )}
    </section>
  );
}
